import React, { useMemo, useState } from 'react';
import {
  DEFAULT_DIAGNOSTIC_PERIOD_ID,
  DIAGNOSTIC_PERIOD_LIST,
} from '../../data/diagnosticPeriodDirectory';
import { BUSINESS_NICHE_LIST, getNicheProfile } from '../../data/businessNicheDirectory';
import {
  DEFAULT_COMBO_SLIDERS,
  HOW_STEPS,
  calculateCombo,
  calculateMarketingDiagnostic,
  formatCount,
  formatDecimal,
  formatMoney,
  getDiagnosticDefaultsForNiche,
} from './marketingDiagnosticLogic';
import RevenueLeversNumberField from '../RevenueLevers/RevenueLeversNumberField';
import '../RevenueLevers/RevenueLevers.css';
import './MarketingDiagnostic.css';

const EFFORT_LABELS = {
  low: 'Легко',
  mid: 'Средне',
  high: 'Сложно',
  ok: 'Ок',
  impossible: 'Нереально',
};

const COMBO_LABELS = {
  l: 'Заявки',
  c: 'Конверсия',
  s: 'Чеки',
  d: 'Глубина чека',
  i: 'Цена позиции',
};

const MarketingDiagnostic = () => {
  const locale = 'ru-RU';
  const [nicheId, setNicheId] = useState('');
  const [periodId, setPeriodId] = useState(DEFAULT_DIAGNOSTIC_PERIOD_ID);
  const [inputs, setInputs] = useState({});
  const [sliders, setSliders] = useState(DEFAULT_COMBO_SLIDERS);
  const [activeTab, setActiveTab] = useState('single');

  const formsEnabled = Boolean(nicheId);
  const nicheProfile = nicheId ? getNicheProfile(nicheId) : null;

  const setField = (key) => (value) => setInputs((prev) => ({ ...prev, [key]: value }));

  const handleNicheChange = (event) => {
    const nextId = event.target.value;
    setNicheId(nextId);
    if (nextId) {
      setInputs(getDiagnosticDefaultsForNiche(nextId));
      setSliders(DEFAULT_COMBO_SLIDERS);
      setActiveTab('single');
    } else {
      setInputs({});
    }
  };

  const result = useMemo(
    () => (formsEnabled ? calculateMarketingDiagnostic(inputs, nicheId, periodId) : null),
    [inputs, nicheId, periodId, formsEnabled],
  );
  const combo = useMemo(
    () => (formsEnabled ? calculateCombo(inputs, sliders, nicheId, periodId) : null),
    [inputs, sliders, nicheId, periodId, formsEnabled],
  );

  const targetBadge = useMemo(() => {
    if (!result || result.R <= 0 || result.Rt <= 0) return null;
    const pct = result.targetGrowthPct;
    if (pct > 0) {
      return {
        text: `нужно +${pct.toFixed(0)}% · +${formatMoney(result.targetGrowthAbs, locale)} за период`,
        good: true,
      };
    }
    if (pct < 0) {
      return { text: `цель ниже текущей на ${Math.abs(pct).toFixed(0)}%`, good: false };
    }
    return { text: 'цель совпадает с текущей выручкой', good: true };
  }, [result, locale]);

  const formatLeverCur = (row) => {
    if (row.key === 'c') return `${row.cur.toFixed(1)}%`;
    if (row.key === 'p' || row.key === 'i') return formatMoney(row.cur, locale);
    if (row.key === 'd') return `${formatDecimal(row.cur, locale)} ${'поз.'}`;
    if (row.key === 's' || row.key === 'l') return `${formatCount(row.cur, locale)} ${'шт'}`;
    return `${formatCount(row.cur, locale)} ${'шт'}`;
  };

  const formatLeverNeed = (row) => {
    if (row.impossible) return 'невозможно >100%';
    if (row.need == null) return '—';
    if (row.key === 'c') return `${row.need.toFixed(1)}%`;
    if (row.key === 'p' || row.key === 'i') return formatMoney(row.need, locale);
    if (row.key === 'd') return `${formatDecimal(row.need, locale)} ${'поз.'}`;
    if (row.key === 's' || row.key === 'l') return `${formatCount(row.need, locale)} ${'шт'}`;
    return `${formatCount(row.need, locale)} ${'шт'}`;
  };

  const formatLeverChange = (row) => {
    if (row.goalReached) return 'цель уже достигнута';
    if (row.pct == null) return '—';
    return (
      <>
        <div>{row.pct >= 0 ? '+' : ''}{row.pct.toFixed(0)}%</div>
        {row.abs != null && (
          <div className="md-lever-abs">
            {row.key === 'c'
              ? `${row.abs >= 0 ? '+' : ''}${row.abs.toFixed(1)} п.п.`
              : row.key === 'p' || row.key === 'i'
                ? `${row.abs >= 0 ? '+' : ''}${formatMoney(row.abs, locale)}`
                : row.key === 'd'
                  ? `${row.abs >= 0 ? '+' : ''}${formatDecimal(row.abs, locale)} ${'поз.'}`
                  : `${row.abs >= 0 ? '+' : ''}${formatCount(row.abs, locale)} ${'шт'}`}
          </div>
        )}
      </>
    );
  };

  const COMBO_DOTS = { l: 'dl', c: 'dc', s: 'dq', d: 'dd', i: 'di' };

  const formatComboRowCur = (row) => {
    if (row.key === 'c') return `${row.cur.toFixed(1)}%`;
    if (row.key === 'i') return formatMoney(row.cur, locale);
    if (row.key === 'd') return `${formatDecimal(row.cur, locale)} ${'поз.'}`;
    return `${formatCount(row.cur, locale)} ${'шт'}`;
  };

  const formatComboRowNew = (row) => {
    if (row.key === 'c') return `${row.cur.toFixed(1)}% → ${row.new.toFixed(1)}%`;
    if (row.key === 'i') return `${formatMoney(row.cur, locale)} → ${formatMoney(row.new, locale)}`;
    if (row.key === 'd') {
      return `${formatDecimal(row.cur, locale)} → ${formatDecimal(row.new, locale)} ${'поз.'}`;
    }
    return `${formatCount(row.cur, locale)} → ${formatCount(Math.round(row.new), locale)} ${'шт'}`;
  };

  const formatComboRowAbs = (row) => {
    if (row.pct <= 0) return 'без изменений';
    const delta = row.new - row.cur;
    if (row.key === 'c') return `+${delta.toFixed(1)} ${'п.п.'}`;
    if (row.key === 'i') return `+${formatMoney(delta, locale)}`;
    if (row.key === 'd') return `+${formatDecimal(delta, locale)} ${'поз.'}`;
    return `+${formatCount(Math.round(delta), locale)} ${'шт'}`;
  };

  const fieldDisabled = !formsEnabled;

  return (
    <div className="md-tool">
      <div className="rl-legend">
        <div className="rl-legend-item"><span className="rl-legend-dot rl-legend-dot--input" />{'Вводите вы'}</div>
        <div className="rl-legend-item"><span className="rl-legend-dot rl-legend-dot--calc" />{'Считается автоматически'}</div>
      </div>

      <div className="rl-panel md-niche-panel">
        <div className="rl-panel-title">{'Тип бизнеса'}</div>
        <div className="rl-field">
          <div className="rl-field-label">
            <span>
              {'Тип ниши'}
              <span className="rl-field-required" aria-hidden="true"> *</span>
            </span>
          </div>
          <div className="rl-input-wrap rl-input-wrap--select">
            <select
              className={`rl-input rl-select${nicheId ? '' : ' rl-select--placeholder'}`}
              value={nicheId}
              onChange={handleNicheChange}
            >
              <option value="">{'Выберите тип бизнеса'}</option>
              {BUSINESS_NICHE_LIST.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.label}
                </option>
              ))}
            </select>
          </div>
          {nicheProfile ? (
            <>
              <div className="rl-field-note">{nicheProfile.desc}</div>
              <div className="rl-field-note md-niche-examples">{nicheProfile.examples}</div>
            </>
          ) : (
            <div className="md-niche-required">{'Сначала выберите тип бизнеса — от этого зависят подсказки и расчёты.'}</div>
          )}
        </div>
      </div>

      <div className={`md-forms${formsEnabled ? '' : ' md-forms--locked'}`}>
        <div className="md-section-head">
          <div className="rl-section-title md-section-head__title">{'Текущие показатели'}</div>
          <div className="md-period-field">
            <label className="md-period-label" htmlFor="md-period-select">{'Период'}</label>
            <div className="rl-input-wrap rl-input-wrap--select md-period-select-wrap">
              <select
                id="md-period-select"
                className="rl-input rl-select md-period-select"
                value={periodId}
                onChange={(e) => setPeriodId(e.target.value)}
                disabled={fieldDisabled}
              >
                {DIAGNOSTIC_PERIOD_LIST.map((period) => (
                  <option key={period.id} value={period.id}>
                    {period.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <p className="md-period-hint">{'Все суммы и количества ниже — за выбранный период.'}</p>
        <div className="md-grid md-grid--2">
          <div className="rl-panel">
            <div className="rl-panel-title">{'Поток клиентов'}</div>
            <RevenueLeversNumberField label={'Лидов в месяц'} unit={'шт'} value={inputs.leads ?? 0} onChange={setField('leads')} min={0} note={'заявки, обращения'} disabled={fieldDisabled} />
            <RevenueLeversNumberField label={'Конверсия лид → продажа'} unit={'%'} value={inputs.conv ?? 0} onChange={setField('conv')} min={0.1} max={100} step={0.5} note={'лид → сделка'} disabled={fieldDisabled} />
            <RevenueLeversNumberField label={'Продажи / чеки за период'} unit={'шт'} value={inputs.sales ?? 0} onChange={setField('sales')} min={0} note={'Все покупки за период: новые и из базы'} disabled={fieldDisabled} />
            <RevenueLeversNumberField label={'Покупок за всё время клиента'} unit={'раз'} value={inputs.ltvQ ?? 1} onChange={setField('ltvQ')} min={1} step={0.5} note={'Сколько раз один клиент покупает, пока с вами'} disabled={fieldDisabled} />
            <RevenueLeversNumberField label={'Первичный отток клиентов'} unit={'%'} value={inputs.churn ?? 0} onChange={setField('churn')} min={0} max={100} note={'Доля потерянных покупателей до первой сделки (нет в наличии, нет условий). Не знаете — ставьте 5%'} disabled={fieldDisabled} />
          </div>
          <div className="rl-panel">
            <div className="rl-panel-title">{'Деньги и ценность клиента'}</div>
            <RevenueLeversNumberField label={'Выручка с рекламных лидов'} unit={'₸'} value={inputs.revenue ?? 1} onChange={setField('revenue')} min={1} note={'Сумма за выбранный период. Средний чек считается автоматически'} disabled={fieldDisabled} />
            <RevenueLeversNumberField label={'Глубина чека'} unit={'поз.'} value={inputs.checkDepth ?? 1} onChange={setField('checkDepth')} min={1} step={0.01} note={'Среднее число позиций в одном чеке. Кол-во позиций / кол-во чеков за период'} disabled={fieldDisabled} />
            <RevenueLeversNumberField label={'Маржинальность'} unit={'%'} value={inputs.margin ?? 1} onChange={setField('margin')} min={1} max={99} note={'Для услуг обычно 50–80%'} disabled={fieldDisabled} />
          </div>
        </div>

        <div className="md-grid md-grid--2 md-mt">
          <div className="rl-panel">
            <div className="rl-panel-title">{'Реклама'}</div>
            <RevenueLeversNumberField label={'Рекламный бюджет в месяц'} unit={'₸'} value={inputs.budget ?? 0} onChange={setField('budget')} min={0} note={'Таргет, контекст, агентство'} disabled={fieldDisabled} />
            <RevenueLeversNumberField label={'Доля на привлечение'} unit={'%'} value={inputs.cacShare ?? 5} onChange={setField('cacShare')} min={5} max={80} note={'30% — стандарт для устойчивого бизнеса'} disabled={fieldDisabled} />
          </div>
          <div className="rl-panel">
            <div className="rl-panel-title">{'Ваша цель'}</div>
            <RevenueLeversNumberField label={'Целевая выручка за период'} unit={'₸'} value={inputs.revenueTarget ?? 1} onChange={setField('revenueTarget')} min={1} disabled={fieldDisabled} />
            {targetBadge ? (
              <div className={`md-target-badge${targetBadge.good ? '' : ' md-target-badge--bad'}`}>
                {targetBadge.text}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {!formsEnabled ? null : (
        <>
          {result.dataError ? (
            <div className="md-insight md-insight--bad">{result.dataError}</div>
          ) : null}

          <div className="rl-section-title">{'Клиенты и структура чека'}</div>
          <div className="md-grid md-grid--4">
            <MetricCard label={'Средний чек'} value={result.P > 0 ? formatMoney(result.P, locale) : '—'} formula={result.formulas.avgCheck} />
            <MetricCard label={'Средняя стоимость позиции в чеке'} value={result.I > 0 ? formatMoney(result.I, locale) : '—'} formula={result.formulas.itemPrice} />
            <MetricCard label={'Кол-во клиентов'} value={result.CL > 0 ? formatCount(Math.round(result.CL), locale) : '—'} formula={result.formulas.clients} />
            <MetricCard
              label={'Продаж в день'}
              value={result.salesPerDay > 0 ? `${formatDecimal(result.salesPerDay, locale)} ${'в день'}` : '—'}
              formula={result.formulas.salesPerDay ? `${result.formulas.salesPerDay} ${'дн'}` : null}
            />
          </div>

          <div className="rl-section-title">{'Цифры для рекламы'}</div>
          <div className="md-grid md-grid--4">
            <MetricCard label={'Стоимость заявки'} value={result.CPL > 0 ? formatMoney(result.CPL, locale) : '—'} formula={result.formulas.cpl} />
            <MetricCard label={'Конверсия заявка → клиент'} value={result.C > 0 ? `${(result.C * 100).toFixed(1)}%` : '—'} formula={result.formulas.cr} />
            <MetricCard label={'Стоимость привлечения'} value={result.CAC > 0 ? formatMoney(result.CAC, locale) : '—'} formula={result.formulas.cac} />
            <MetricCard label={'Макс. стоимость заявки'} value={result.maxCPL > 0 ? formatMoney(result.maxCPL, locale) : '—'} formula={result.formulas.maxCpl} accent="warn" />
          </div>
          {result.adsInsight ? (
            <div className={`md-insight md-insight--${result.adsInsight.tone}`}>{result.adsInsight.text}</div>
          ) : null}

          <div className="rl-section-title">{'Окупаемость'}</div>
          <div className="md-grid md-grid--3">
            <MetricCard label={'Заработок с клиента за всё время'} value={result.LTV > 0 ? formatMoney(result.LTV, locale) : '—'} formula={result.formulas.ltv} />
            <MetricCard label={'Максимум на привлечение'} value={result.maxCAC > 0 ? formatMoney(result.maxCAC, locale) : '—'} formula={result.formulas.maxCac} />
            <MetricCard
              label={'Окупаемость рекламы'}
              value={result.CAC > 0 ? `${Math.round(result.ROMI)}%` : '—'}
              formula={result.formulas.romi}
              valueClass={result.ROMI >= 200 ? 'good' : result.ROMI >= 0 ? 'warn' : 'bad'}
              accent="good"
            />
          </div>

          <div className="rl-section-title">{'Соотношение заработка и затрат'}</div>
          <div className="md-grid md-grid--4">
            <MetricCard
              label={'Клиент приносит vs стоит привлечение'}
              value={result.ltvCac > 0 ? `${result.ltvCac.toFixed(1)}×` : '—'}
              formula={result.formulas.ltvCac}
              valueClass={
                result.ltvCac >= result.benchmarks.ltvCacGood
                  ? 'good'
                  : result.ltvCac >= result.benchmarks.ltvCacWarn
                    ? 'warn'
                    : 'bad'
              }
            />
            <MetricCard label={'Доля рекламы от выручки'} value={result.budgetShare > 0 ? `${result.budgetShare.toFixed(1)}%` : '—'} formula={result.formulas.budgetShare} />
            <MetricCard label={'Прибыль за период'} value={result.profit > 0 ? formatMoney(result.profit, locale) : '—'} formula={result.formulas.profit} />
            <MetricCard label={'Прибыль на 1 продажу'} value={result.profitPerSale > 0 ? formatMoney(result.profitPerSale, locale) : '—'} formula={result.formulas.profitPerSale} />
          </div>
          {result.roiInsight ? (
            <div className={`md-insight md-insight--${result.roiInsight.tone}`}>{result.roiInsight.text}</div>
          ) : null}
          {result.budgetInsight ? (
            <div className={`md-insight md-insight--${result.budgetInsight.tone}`}>{result.budgetInsight.text}</div>
          ) : null}

          <div className="rl-section-title">{'Рычаги роста до цели'}</div>
          <div className="md-tabs">
            <button type="button" className={`md-tab${activeTab === 'single' ? ' md-tab--active' : ''}`} onClick={() => setActiveTab('single')}>{'По одному рычагу'}</button>
            <button type="button" className={`md-tab${activeTab === 'combo' ? ' md-tab--active' : ''}`} onClick={() => setActiveTab('combo')}>{'Все рычаги вместе'}</button>
          </div>

          {activeTab === 'single' ? (
            <>
              <div className="rl-table-wrap">
                <table className="rl-table md-lever-table">
                  <thead>
                    <tr>
                      <th>{'Рычаг'}</th>
                      <th>{'Сейчас'}</th>
                      <th>{'Нужно'}</th>
                      <th>{'Изменение'}</th>
                      <th>{'Усилие'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.levers.map((row) => (
                      <tr
                        key={row.key}
                        className={
                          row.easeRank === 1
                            ? 'md-lever-best'
                            : row.easeRank === 2
                              ? 'md-lever-tier2'
                              : row.easeRank === 3
                                ? 'md-lever-tier3'
                                : undefined
                        }
                      >
                        <td>
                          <div className="md-lever-name"><span className={`md-dot md-dot--${row.dot}`} />{row.name}</div>
                          <div className="md-lever-desc">{row.desc}</div>
                          {row.easeRank === 1 ? <div className="md-best-lbl">{'← проще всего достичь цели'}</div> : null}
                          {row.easeRank === 2 ? <div className="md-tier-lbl md-tier-lbl--2">{'← тоже реально · 2-й по простоте'}</div> : null}
                          {row.easeRank === 3 ? <div className="md-tier-lbl md-tier-lbl--3">{'← тоже реально · 3-й по простоте'}</div> : null}
                        </td>
                        <td>{formatLeverCur(row)}</td>
                        <td>{formatLeverNeed(row)}</td>
                        <td>{formatLeverChange(row)}</td>
                        <td>{row.effort ? <span className={`md-eff md-eff--${row.effort}`}>{EFFORT_LABELS[row.effort]}</span> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="md-note">{'Каждая строка — сценарий: рост только по одному показателю на % из цели. При равном % приоритет: глубина чека → продажи → средний чек → цена позиции.'}</p>
            </>
          ) : (
            <div className="md-grid md-grid--combo">
              <div className="rl-panel md-combo-panel">
                <div className="rl-panel-title">{'Задайте рост по каждому показателю'}</div>
                {combo.leverRows.map((row) => {
                  const max = row.key === 'l' ? 200 : 100;
                  return (
                    <div key={row.key} className="md-combo-row">
                      <div className="md-combo-col md-combo-col--meta">
                        <div className="md-combo-main">
                          <span className={`md-dot md-dot--${COMBO_DOTS[row.key]}`} />
                          <span className="md-combo-title">{COMBO_LABELS[row.key]}</span>
                        </div>
                        <div className="md-combo-hint md-combo-hint--indent">{`сейчас: ${formatComboRowCur(row)}`}</div>
                      </div>
                      <div className="md-combo-col md-combo-col--slider">
                        <div className="md-combo-main md-combo-slider">
                          <input
                            type="range"
                            className={`md-range md-range--${row.key}`}
                            min={0}
                            max={max}
                            step={5}
                            value={sliders[row.key]}
                            onChange={(e) => setSliders((prev) => ({ ...prev, [row.key]: +e.target.value }))}
                          />
                          <span className={`md-combo-pct md-combo-pct--${row.key}`}>+{row.pct}%</span>
                        </div>
                      </div>
                      <div className="md-combo-col md-combo-col--result">
                        <div className={`md-combo-main md-combo-new md-combo-new--${row.key}`}>{formatComboRowNew(row)}</div>
                        <div className="md-combo-hint md-combo-hint--right">{formatComboRowAbs(row)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div>
                <div className="md-mcard md-mt">
                  <div className="md-mcard-label">{'Выручка при этих изменениях'}</div>
                  <div className="md-mcard-val">{formatMoney(combo.newRev, locale)}</div>
                  <div className="md-mcard-formula">{combo.formula}</div>
                </div>
                <div className="md-mcard">
                  <div className="md-mcard-label">{'Достигаете цели?'}</div>
                  <div className={`md-mcard-val${combo.reached ? ' good' : ' warn'}`}>{combo.reached ? 'Да ✓' : 'Ещё нет'}</div>
                  <div className="md-mcard-formula">
                    {combo.reached
                      ? `+${formatMoney(combo.gap, locale)} сверх цели`
                      : `до цели не хватает ${formatMoney(Math.abs(combo.gap), locale)}`}
                  </div>
                </div>
                <div className="md-insight md-insight--neutral">{'Выручка = чеки × глубина × цена позиции. Заявки и конверсия растят продажи от новых клиентов, остальные рычаги — повторные продажи и чек. Показатели перемножаются.'}</div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="rl-how">
        <div className="rl-panel-title">{'Как считается'}</div>
        {HOW_STEPS.map((step) => (
          <div key={step.num} className="rl-how-step">
            <div className="rl-how-num">{step.num}</div>
            <div className="rl-how-text"><strong>{step.title}</strong> {step.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, formula, accent, valueClass }) => (
  <div className={`md-mcard${accent === 'warn' ? ' md-mcard--warn' : accent === 'good' ? ' md-mcard--good' : ''}`}>
    <div className="md-mcard-label">{label}</div>
    <div className={`md-mcard-val${valueClass ? ` ${valueClass}` : ''}`}>{value}</div>
    {formula ? <div className="md-mcard-formula">{formula}</div> : null}
  </div>
);

export default MarketingDiagnostic;
